/**
 * Static Result Group
 *
 * Renders a cmdk <Command.Group> of client-side command-registry entries
 * ("Go to", Settings, Actions) — the instant, zero-latency half of the
 * Cmd-K palette. Row layout mirrors SearchResultItem: leading icon | label |
 * faint right-aligned category tag.
 */

import { Box, HStack, Text, VisuallyHidden } from '@chakra-ui/react';
import { Command } from 'cmdk';
import { Glyph } from '../../ui/Glyph';
import { MaterialIcon } from '../../ui/MaterialIcon';
import type { CommandEntry, CommandGroup } from '../../../constants/commandRegistry';

interface StaticResultGroupProps {
  heading: string;
  entries: readonly CommandEntry[];
  onSelect: (entry: CommandEntry) => void;
}

/** Faint right-aligned category tag derived from the entry's group. */
const GROUP_TAG_LABEL: Record<CommandGroup, string> = {
  goto: 'Go to',
  settings: 'Settings',
  help: 'Help',
  action: 'Action',
};

/**
 * The row's accessible name: the label plus the category tag the sighted user
 * reads on the right, and — for help rows — the "opens in a new tab" warning
 * that is otherwise only in a `VisuallyHidden` sibling. Deliberately EXCLUDES
 * the snippet, which is carried by `aria-describedby` instead.
 */
function ariaLabelFor(entry: CommandEntry): string {
  const tag = GROUP_TAG_LABEL[entry.group];
  return entry.group === 'help'
    ? `${entry.label}, ${tag}, opens in a new tab`
    : `${entry.label}, ${tag}`;
}

export function StaticResultGroup({ heading, entries, onSelect }: StaticResultGroupProps) {
  // Return null when empty so no bare header renders.
  if (entries.length === 0) return null;

  return (
    <Command.Group heading={heading} forceMount>
      {entries.map((entry) => (
        <Command.Item
          key={entry.id}
          value={entry.id}
          onSelect={() => onSelect(entry)}
          style={{ cursor: 'pointer' }}
          // `role="option"` is name-FROM-CONTENT, so an inlined snippet lands in
          // the row's accessible name — up to 240 chars of prose read on every
          // arrow keypress, while the visible text is clamped to one line.
          // `aria-describedby` alone does NOT fix that: a description is
          // additive, so the snippet would be announced twice. An explicit
          // `aria-label` is what actually replaces the name; the snippet then
          // reaches AT once, as the description.
          aria-label={ariaLabelFor(entry)}
          aria-describedby={entry.description ? `${entry.id}-snippet` : undefined}
        >
          <HStack px={3} py={3} spacing={3} w="100%" align="center">
            {/* ONE glyph field, so one treatment. The entry's `glyph` carries
                which library holds the asset, which is not a styling decision
                — and while that branch was written out here it read like one:
                the Bower arm carried the gradient and the Material arm stayed
                flat, so whether a command row was painted came down to where
                its glyph happened to live. */}
            <Glyph {...entry.glyph} size="20px" gradient="warm" />

            {/* Two-line only when the entry carries a description (live
                help-centre hits). Registry entries omit it and keep the
                original single-line row exactly as it was. */}
            <Box flex={1} minW={0}>
              <Text fontSize="sm" color="text.primary" noOfLines={1}>
                {entry.label}
              </Text>
              {/* `text.onMutedBg`, not `text.muted`: there is ALWAYS a
                  highlighted row, and on cmdk's `bg.hover` the muted token is
                  3.79:1 — below AA for 12px text. This one clears AA on the
                  idle, hovered and active backgrounds in both modes. */}
              {entry.description && (
                <Text id={`${entry.id}-snippet`} fontSize="xs" color="text.onMutedBg" noOfLines={1}>
                  {entry.description}
                </Text>
              )}
            </Box>

            <HStack spacing={1.5} flexShrink={0}>
              <Text fontSize="xs" color="text.muted" whiteSpace="nowrap">
                {GROUP_TAG_LABEL[entry.group]}
              </Text>
              {/* Help rows leave the app for docs.bowerlabs.ai. Saying so before
                  the click is the difference between "opened a doc" and "where
                  did my palette go". */}
              {entry.group === 'help' && (
                <>
                  {/* `aria-hidden` + a real hidden string, NOT `aria-label` on
                      the icon. MaterialIcon renders a span whose TEXT CONTENT
                      is the ligature ("open_in_new"), and aria-label is
                      name-prohibited on a generic span — so a screen reader
                      was liable to read the ligature into the row's name. */}
                  <MaterialIcon name="open_in_new" boxSize={3.5} color="text.muted" aria-hidden />
                  <VisuallyHidden>Opens in a new tab</VisuallyHidden>
                </>
              )}
            </HStack>
          </HStack>
        </Command.Item>
      ))}
    </Command.Group>
  );
}
